import { useEffect, useMemo, useState } from 'react';
import RenderIf from '../RenderIf';
import {
  Divider,
  Tab,
  TabList,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import type { IFieldsTab } from '@moko/utils';

import type { IOtherInfoProps } from './types';
import { OtherInfoProvider, useOtherInfoContext } from './OtherInfoContext';
import { FormArea } from './FormArea';
import { FieldList } from './FieldList';
import { MobileOtherInfo } from './MobileOtherInfo';
import { DesktopOtherInfo } from './DesktopOtherInfo';
import useForceUpdate from '../hooks/useForceUpdate';
import { useThemedTabs } from '../theme/useThemedTabs';
const OtherInfoContent = () => {
  const { effectsKey, params } = useOtherInfoContext();
  const tabListClass = useThemedTabs();
  const [selectedTab, setSelectedTab] = useState<string>('');

  const visibleTabs = useMemo(
    () => (params.metadata.tabs ?? []).filter((tab) => !tab.hidden),
    [params.metadata.tabs, effectsKey]
  );

  useEffect(() => {
    if (visibleTabs.length === 0) {
      if (selectedTab !== '') {
        setSelectedTab('');
      }
      return;
    }

    if (!visibleTabs.some((tab) => tab.tabName === selectedTab)) {
      setSelectedTab(visibleTabs[0]?.tabName ?? '');
    }
  }, [visibleTabs, selectedTab]);

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    const next = data.value as string;
    if (next === selectedTab) return;
    void (async () => {
      if (params.persistForm) {
        const ok = await params.persistForm({ skipIfClean: true });
        if (!ok) return;
      }
      setSelectedTab(next);
    })();
  };

  const hasTabs = visibleTabs.length > 0;

  const currentTabFields = useMemo(() => {
    if (visibleTabs.length === 1) {
      const tabName = visibleTabs[0]?.tabName;
      return params.metadata.fields.filter((f) => f.tabName === tabName);
    }
    return hasTabs && selectedTab
      ? params.metadata.fields.filter((f) => f.tabName === selectedTab)
      : params.metadata.fields;
  }, [hasTabs, selectedTab, params.metadata.fields, visibleTabs]);

  return (
    <FormArea
      style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '14px',
      }}
    >
      <RenderIf condition={!params.hideTitle}>
        <Divider>Company-specific info</Divider>
      </RenderIf>
      {!params.metadata || !params.otherInfo ? (
        <></>
      ) : !hasTabs || visibleTabs.length === 1 ? (
        <FieldList fields={currentTabFields} parentName="root" />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <TabList
              className={tabListClass}
              selectedValue={selectedTab}
              onTabSelect={onTabSelect}
            >
              {visibleTabs.map((tab: IFieldsTab) => (
                <Tab key={tab.tabName} value={tab.tabName}>
                  {tab.caption}
                </Tab>
              ))}
            </TabList>
          </div>
          <div
            style={{
              paddingTop: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <FieldList fields={currentTabFields} parentName="root" />
            {params.renderTab?.(selectedTab)}
          </div>
        </>
      )}
    </FormArea>
  );
};

const DesktopSplitOtherInfoContent = () => {
  const { params } = useOtherInfoContext();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '14px',
        width: '100%',
      }}
    >
      <RenderIf condition={!params.hideTitle}>
        <Divider>Company-specific info</Divider>
      </RenderIf>
      {!params.metadata || !params.otherInfo ? <></> : <DesktopOtherInfo />}
    </div>
  );
};

const OtherInfo = (params: IOtherInfoProps) => {
  const [, rerenderOtherInfo] = useForceUpdate();

  if (params.mode === 'mobile') {
    return (
      <OtherInfoProvider params={params} rerenderOtherInfo={rerenderOtherInfo}>
        <div
          style={{
            justifySelf: 'stretch',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            rowGap: params.contained ? 0 : '14px',
            ...(params.contained
              ? { height: '100%', flex: 1, overflow: 'hidden' }
              : {}),
          }}
        >
          <MobileOtherInfo />
        </div>
      </OtherInfoProvider>
    );
  }

  if (params.mode === 'desktop-split') {
    return (
      <OtherInfoProvider params={params} rerenderOtherInfo={rerenderOtherInfo}>
        <DesktopSplitOtherInfoContent />
      </OtherInfoProvider>
    );
  }

  return (
    <OtherInfoProvider params={params} rerenderOtherInfo={rerenderOtherInfo}>
      <OtherInfoContent />
    </OtherInfoProvider>
  );
};

export default OtherInfo;
